import React, { useState, useEffect, useCallback } from 'react';
import { WIZARD_STAGES, WizardProgress } from '../../types/profile';
import { profileService } from '../../services/api';
import WizardProgressBar from './WizardProgressBar';
import Stage1Essentials from './stages/Stage1Essentials';
import Stage2AboutYou from './stages/Stage2AboutYou';
import Stage3Lifestyle from './stages/Stage3Lifestyle';
import Stage4Interests from './stages/Stage4Interests';
import Stage5Prompts from './stages/Stage5Prompts';
import Stage6Media from './stages/Stage6Media';
import Stage7Gaming from './stages/Stage7Gaming';
import './Wizard.css';

interface WizardContainerProps {
  onComplete: () => void;
  onSkip?: () => void;
  initialStage?: number;
}

export interface StageProps {
  onNext: (data?: Record<string, unknown>) => void;
  onBack: () => void;
  onSkip: () => void;
  isLoading: boolean;
}

const WizardContainer: React.FC<WizardContainerProps> = ({
  onComplete,
  onSkip,
  initialStage = 1,
}) => {
  const [currentStage, setCurrentStage] = useState(initialStage);
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState<WizardProgress | null>(null);
  const [completionPercentage, setCompletionPercentage] = useState(0);

  // Load initial progress
  useEffect(() => {
    const loadProgress = async () => {
      const status = await profileService.getCompletionStatus();
      if (status?.wizardProgress) {
        setProgress(status.wizardProgress);
        setCompletionPercentage(status.completionPercentage);
        if (status.wizardProgress.currentStage > 1) {
          setCurrentStage(status.wizardProgress.currentStage);
          setCurrentStep(status.wizardProgress.currentStep);
        }
      }
    };
    loadProgress();
  }, []);

  const handleNext = useCallback(async (stageData?: Record<string, unknown>) => {
    setIsLoading(true);

    try {
      const currentStageConfig = WIZARD_STAGES[currentStage - 1];
      const totalSteps = currentStageConfig?.steps.length || 1;

      if (currentStep < totalSteps) {
        // Move to next step within current stage
        setCurrentStep(currentStep + 1);
      } else {
        // Complete current stage and move to next
        const result = await profileService.submitWizardStage(currentStage, {
          step: 0, // 0 indicates stage completion
          data: stageData,
        });

        if (result) {
          setCompletionPercentage(result.completionPercentage);

          if (result.allCompleted || currentStage >= WIZARD_STAGES.length) {
            onComplete();
          } else {
            setCurrentStage(currentStage + 1);
            setCurrentStep(1);
          }
        }
      }
    } catch (error) {
      console.error('Failed to save wizard progress:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentStage, currentStep, onComplete]);

  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else if (currentStage > 1) {
      setCurrentStage(currentStage - 1);
      const prevStageConfig = WIZARD_STAGES[currentStage - 2];
      setCurrentStep(prevStageConfig?.steps.length || 1);
    }
  }, [currentStage, currentStep]);

  const handleSkip = useCallback(() => {
    const currentStageConfig = WIZARD_STAGES[currentStage - 1];

    // Can only skip non-required stages
    if (!currentStageConfig?.isRequired) {
      if (currentStage >= WIZARD_STAGES.length) {
        onComplete();
      } else {
        setCurrentStage(currentStage + 1);
        setCurrentStep(1);
      }
    }
  }, [currentStage, onComplete]);

  const handleSkipAll = useCallback(() => {
    if (onSkip) {
      onSkip();
    } else if (progress?.stage1Completed) {
      // Only allow skip if essentials are done
      onComplete();
    }
  }, [onSkip, onComplete, progress]);

  const stageProps: StageProps = {
    onNext: handleNext,
    onBack: handleBack,
    onSkip: handleSkip,
    isLoading,
  };

  const renderStage = () => {
    switch (currentStage) {
      case 1:
        return <Stage1Essentials {...stageProps} step={currentStep} />;
      case 2:
        return <Stage2AboutYou {...stageProps} step={currentStep} />;
      case 3:
        return <Stage3Lifestyle {...stageProps} step={currentStep} />;
      case 4:
        return <Stage4Interests {...stageProps} step={currentStep} />;
      case 5:
        return <Stage5Prompts {...stageProps} step={currentStep} />;
      case 6:
        return <Stage6Media {...stageProps} step={currentStep} />;
      case 7:
        return <Stage7Gaming {...stageProps} step={currentStep} />;
      default:
        return <Stage1Essentials {...stageProps} step={currentStep} />;
    }
  };

  const currentStageConfig = WIZARD_STAGES[currentStage - 1];
  const canSkip = !currentStageConfig?.isRequired;
  const canSkipAll = progress?.stage1Completed;

  return (
    <div className="wizard-container">
      <div className="wizard-header">
        <WizardProgressBar
          stages={WIZARD_STAGES}
          currentStage={currentStage}
          currentStep={currentStep}
          completionPercentage={completionPercentage}
        />

        <div className="wizard-stage-info">
          <h2 className="wizard-stage-title">
            Stage {currentStage}: {currentStageConfig?.title}
          </h2>
          <p className="wizard-stage-description">
            {currentStageConfig?.description}
          </p>
          {!currentStageConfig?.isRequired && (
            <span className="wizard-optional-badge">Optional</span>
          )}
        </div>
      </div>

      <div className="wizard-content">
        {renderStage()}
      </div>

      <div className="wizard-footer">
        <div className="wizard-footer-left">
          {currentStage > 1 && (
            <button
              type="button"
              className="wizard-btn wizard-btn-secondary"
              onClick={handleBack}
              disabled={isLoading}
            >
              Back
            </button>
          )}
        </div>

        <div className="wizard-footer-center">
          {canSkip && (
            <button
              type="button"
              className="wizard-btn wizard-btn-text"
              onClick={handleSkip}
              disabled={isLoading}
            >
              Skip for now
            </button>
          )}
        </div>

        <div className="wizard-footer-right">
          {canSkipAll && currentStage > 1 && (
            <button
              type="button"
              className="wizard-btn wizard-btn-text"
              onClick={handleSkipAll}
              disabled={isLoading}
            >
              Complete later
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WizardContainer;
